import React from 'react';
import { useBulkJob } from '../../../context/BulkJobContext';

// Sits right below MainNav (rendered from AdminLayout) instead of covering
// it, and reflects shared BulkJobContext state — so it keeps showing no
// matter which admin page (Products, Stores, Orders...) is currently open.
//
// v4.61.0 REWRITE: this used to show one combined count across every
// product×store operation — 313 products into 5 stores read as "1,565
// products", which is confusing since only 313 were ever selected. It now
// shows the real selection size once, plus each target store's OWN
// success/total on its own line, so it's obvious the same set of products
// is going into several stores rather than thousands of unique ones.
//
// v4.64.0: that per-store breakdown listed EVERY selected store's row at
// once, which with a couple dozen stores grew into a grid that filled the
// whole screen. At the time, the backend only ever worked on one store at a
// time (processBulkJob's add/remove loops finished a store completely
// before moving to the next), so there was only ever one row worth showing
// "live" — this showed just that one, with a small "store N of M" position.
//
// v4.65.0: 'add' jobs now work through several stores AT ONCE (bounded
// concurrency — see processBulkJob), so "one store is live" stopped being
// true for them. `stores[].active` (set by the backend the moment a worker
// actually starts on a store, cleared once it's done) is what tells this
// apart from "still queued for a free slot" — showing every not-yet-done
// store would otherwise recreate the original wall-of-bars problem, since
// at the very start of a job every store is technically "not done" even
// though only a handful are actually being worked on. 'remove' jobs are
// untouched — still one store at a time — so they keep the older
// first-not-done-store logic, which is still exactly correct for them.
const JobBanner = ({ job, onCancel }) => {
    if (!job || job.status !== 'running') return null;

    const verb = job.type === 'remove' ? 'Removing' : 'Adding';
    const stores = Array.isArray(job.stores) ? job.stores : [];
    // Falls back to the old flat total/current if an in-flight job somehow
    // has no per-store breakdown (e.g. one started right before this
    // change shipped, still resuming) — never shows a blank banner.
    const productsTotal = job.products_total || job.total || 0;

    let visibleStores;
    if (job.type === 'remove') {
        // Exactly the old single-store logic — remove jobs are still
        // strictly sequential, so there is genuinely only ever one live row.
        const activeIndex = stores.findIndex((s) => (s.current || 0) < (s.total || 0));
        const one = activeIndex >= 0 ? stores[activeIndex] : stores[stores.length - 1];
        visibleStores = one ? [{ store: one, pos: (activeIndex >= 0 ? activeIndex : stores.length - 1) + 1 }] : [];
    } else {
        visibleStores = stores
            .map((s, i) => ({ store: s, pos: i + 1 }))
            .filter(({ store: s }) => s.active);
        // A resumed job from before `active` existed, or the brief window
        // between "job just started" and the first store's `active: true`
        // write landing, would otherwise show nothing at all — same
        // fallback the old code used, so the banner is never blank while
        // the job is genuinely still running.
        if (visibleStores.length === 0) {
            const activeIndex = stores.findIndex((s) => (s.current || 0) < (s.total || 0));
            const one = activeIndex >= 0 ? stores[activeIndex] : stores[stores.length - 1];
            if (one) visibleStores = [{ store: one, pos: (activeIndex >= 0 ? activeIndex : stores.length - 1) + 1 }];
        }
    }

    return (
        <div style={{
            background: 'linear-gradient(90deg, #6f5212 0%, #8c6a1c 55%, #b8892c 100%)', color: '#fff', padding: '12px 24px',
            boxShadow: '0 4px 16px rgba(111,82,18,0.35)',
        }}>
            <style>{`@keyframes ms-bulk-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ width: 16, height: 16, border: '2px solid #f3d58a', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'ms-bulk-spin 0.7s linear infinite', flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
                    {verb} {productsTotal} Product{productsTotal === 1 ? '' : 's'}
                    {job.platform === 'shopify'
                        ? (stores.length > 1 ? ` to ${stores.length} Shopify stores` : ' to Shopify')
                        : (stores.length > 1 ? ` into ${stores.length} stores` : '')}
                </span>
                <span style={{ fontSize: 12, color: '#f3d58a', whiteSpace: 'nowrap' }}>
                    ✅ {job.success}{job.skipped > 0 ? ` ⏭️ ${job.skipped}` : ''}{job.failed > 0 ? ` ❌ ${job.failed}` : ''}
                </span>
                <button
                    onClick={onCancel}
                    style={{ marginLeft: 'auto', padding: '5px 14px', background: '#dc2626', border: 'none', borderRadius: 7, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                    ✕ Cancel
                </button>
            </div>

            {/* One row per store CURRENTLY being worked on — several at once
                for a running 'add' job (bounded concurrency), exactly one
                for a 'remove' job (still sequential) — see visibleStores
                above. Each row's own count against its own total, never the
                flattened product×store total. */}
            {visibleStores.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                    {visibleStores.map(({ store: s, pos }) => {
                        const done = (s.current || 0) >= (s.total || 0) && (s.total || 0) > 0;
                        const pct = s.total > 0 ? Math.round(((s.current || 0) / s.total) * 100) : 0;
                        return (
                            <div key={String(s.store_id)} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <span style={{ fontSize: 13, flexShrink: 0 }}>
                                    {done ? '✅' : (s.current > 0 ? '⏳' : '⏸️')}
                                </span>
                                {stores.length > 1 && (
                                    <span style={{ fontSize: 11.5, color: '#f3d58a', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        Store {pos}/{stores.length}
                                    </span>
                                )}
                                <span style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 60 }} title={s.store_name}>
                                    {s.store_name}
                                </span>
                                <div style={{ flex: 1, background: 'rgba(255,255,255,0.22)', borderRadius: 4, height: 5, minWidth: 40 }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: done ? '#22c55e' : '#f3d58a', borderRadius: 4, transition: 'width 0.2s' }} />
                                </div>
                                <span style={{ fontSize: 11.5, color: '#f6e9cc', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {s.current || 0}/{s.total || 0}
                                    {s.skipped > 0 ? ` (${s.skipped} ⏭️)` : ''}
                                    {s.failed > 0 ? ` (${s.failed} ❌)` : ''}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// The WordPress add/remove (a backend job) and the Shopify add (run from the browser) can be running together, so
// each gets its own banner; they share one sticky holder so two of them stack instead of overlapping.
const BulkJobBanner = () => {
    const ctx = useBulkJob();
    return (
        <div style={{ position: 'sticky', top: 'var(--MainNav-height, 56px)', zIndex: 999 }}>
            <JobBanner job={ctx?.job} onCancel={ctx?.cancelJob} />
            <JobBanner job={ctx?.shopifyJob} onCancel={ctx?.cancelShopifyJob} />
        </div>
    );
};

export default BulkJobBanner;
